/**
 * Invoice PTB — mismo esqueleto visual que Invoice PTC (rejilla, pie fijo, relleno mercancía).
 * Particularidades PTB: bilingüe ES/EN, logo PTB, CNPJ, 7 columnas mercancía, fmt pt-BR, Total FOB.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ptbImage from '../../imagenes/ptb.jpg';
import {
  buildInvoicePtcMerchandise,
  etiquetaPtbFromDescription,
  packingListTotals,
  ptbDimsFromPackingRow,
  usdAmountInWords,
} from './invoicePtcMerchandise';
import {
  fetchOrdenCompraById,
  fetchProformaUltima,
  lineasPdfTienenPrecios,
  resolveProformaIds,
} from './generateInvoicePtcPDF';
import { invoiceNumberForHeader } from './invoiceNumberUtils';

const CNPJ_EMPRESA = '26.076.555/0001-00';

/** Destinatario en Invoice PTB cuando emite PTC (segunda impresión / intercompany). */
export const PTC_DESTINATARIO_EN_PTB = {
  clienteNombre: 'PALM TRADE CHILE SPA',
  taxId: '77.710.540-K',
  clienteDireccion:
    'AVENIDA PRESIDENTE KENNEDY 5146 OFICINA 51\nVITACURA\nSANTIAGO\nCHILE',
  pinCode: '',
  clientePhone: '22-2190119',
  remitPaymentText: 'Please Remit Payment to PALM TRADE CHILE SPA',
};

const COMPANY_LINES = [
  'PALM TRADE BRASIL IMPORTACAO E EXPORTACAO LTDA',
  'RODOVIA A280A, 6777, SALA 214-Box 04, KM 04, ITINGA, CEP 89245-000,',
  'Municipio de ARAQUARI-SC',
  'CNPJ-MF sob o nº: 26.076.555/0001-00',
];

const tableGrid = {
  theme: 'grid',
  styles: {
    fontSize: 7,
    cellPadding: { top: 1.2, right: 1.2, bottom: 1.2, left: 1.2 },
    lineColor: [0, 0, 0],
    lineWidth: 0.15,
    textColor: [0, 0, 0],
    valign: 'middle',
    halign: 'center',
  },
  headStyles: {
    fontSize: 6.5,
    fontStyle: 'bold',
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    halign: 'center',
    valign: 'middle',
  },
};

/** Mismas proporciones que Invoice PTC */
const col4Vessel = (w) => ({
  0: { cellWidth: w * 0.35 },
  1: { cellWidth: w * 0.25 },
  2: { cellWidth: w * 0.2 },
  3: { cellWidth: w * 0.2 },
});

const col5SalesWeight = (w) => ({
  0: { cellWidth: w * 0.23 },
  1: { cellWidth: w * 0.29 },
  2: { cellWidth: w * 0.28 },
  3: { cellWidth: w * 0.1 },
  4: { cellWidth: w * 0.1 },
});

const PTB_SIGNATURE_BOTTOM_PAD = 10;
const PTB_SIGNATURE_BLOCK_H = 15 + 2 + 9;
const PTB_SIGNATURE_ZONE_H = PTB_SIGNATURE_BOTTOM_PAD + PTB_SIGNATURE_BLOCK_H;
/** Cabecera mercancía PTB: 2 filas de encabezado + padding autotable. */
const PTB_MERCH_HEAD_EST_H = 22;
/** Margen mínimo para no forzar 2ª página (menor = detalle más cerca de la firma). */
const PTB_FILLER_SAFETY_MM = 2;
/** Fila Total M3 + Total FOB (tabla posterior al detalle). */
const PTB_TOTALS_ROW_EST_H = 8;
const merchBodyVertLines = { top: 0, right: 0.15, bottom: 0, left: 0.15 };
const merchHeadLabel = { fontStyle: 'bold', halign: 'center', valign: 'middle' };
const merchHeadCaps = { ...merchHeadLabel, fontStyle: 'bold' };

function priceCellPtb(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return fmtNumBr(Number(v), 2);
}

function safe(s) {
  return s == null ? '' : String(s);
}

function upper(s) {
  const t = safe(s).trim();
  return t ? t.toUpperCase() : '';
}

/** Formato numérico factura PTB (miles con punto, decimales con coma). */
export function fmtNumBr(v, dec = 2) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  }).format(Number(v));
}

function fmtDateLongEn(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + (isoDate.length === 10 ? 'T12:00:00' : ''));
  if (Number.isNaN(d.getTime())) return safe(isoDate);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Fallback: segunda línea del bloque descripción (ej. "3.1 MM X 1220 X 2440"). */
function parseDimsFromDescriptionLine(raw) {
  const parts = safe(raw)
    .replace(/\bmm\b/gi, '')
    .replace(/\s+/g, '')
    .split(/[xX]/)
    .map((p) => p.trim())
    .filter(Boolean);
  return {
    espessura: parts[0] || '',
    largura: parts[1] || '',
    comprimento: parts[2] || '',
  };
}

function ptbDimsForMercanciaLine(ln) {
  if (ln.espessura || ln.largura || ln.comprimento) {
    return {
      espessura: safe(ln.espessura),
      largura: safe(ln.largura),
      comprimento: safe(ln.comprimento),
    };
  }
  const fromRow = ptbDimsFromPackingRow(ln.sampleRow);
  if (fromRow.espessura || fromRow.largura || fromRow.comprimento) return fromRow;
  const descLines = safe(ln.descriptionBlock).split(/\r?\n/);
  if (descLines[1]) return parseDimsFromDescriptionLine(descLines[1]);
  return { espessura: '', largura: '', comprimento: '' };
}

function mapLineasMercanciaToPtbRows(lineasMercancia) {
  return (lineasMercancia || []).map((ln) => {
    const dims = ptbDimsForMercanciaLine(ln);
    const desc = safe(ln.descriptionBlock || ln.description || ln.productName).trim();
    const qty =
      ln.quantityM3 != null && !Number.isNaN(Number(ln.quantityM3))
        ? Number(ln.quantityM3)
        : null;
    const unit =
      ln.unitPriceUsd != null && !Number.isNaN(Number(ln.unitPriceUsd))
        ? Number(ln.unitPriceUsd)
        : ln.unitPrice != null && !Number.isNaN(Number(ln.unitPrice))
          ? Number(ln.unitPrice)
          : null;
    let total =
      ln.totalValueUsd != null && !Number.isNaN(Number(ln.totalValueUsd))
        ? Number(ln.totalValueUsd)
        : ln.totalValue != null && !Number.isNaN(Number(ln.totalValue))
          ? Number(ln.totalValue)
          : null;
    if (total == null && qty != null && unit != null) {
      total = Math.round(qty * unit * 100) / 100;
    }
    return {
      quantityM3: qty,
      etiqueta: upper(
        ln.etiquetaPtb ||
          etiquetaPtbFromDescription(
            desc.split(/\r?\n/)[0],
            ln.productName
          )
      ),
      ...dims,
      unitPriceUsd: unit,
      totalValueUsd: total,
    };
  });
}

function lineasFromFormulario(f, inv) {
  const saved = f?.lineasMercancia;
  if (Array.isArray(saved) && saved.length) {
    const mapped = mapLineasMercanciaToPtbRows(saved);
    if (lineasPdfTienenPrecios(mapped)) return mapped;
  }
  return [];
}

function totalDueWordsPtb(amount) {
  const n = Math.round(Number(amount) * 100) / 100;
  if (!Number.isFinite(n) || n <= 0) return '';
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  const words = usdAmountInWords(whole)
    .replace(/\s+DOLLARS?$/i, '')
    .trim();
  return `${words}, ${String(cents).padStart(2, '0')}/100 USD DOLLARS`;
}

function ptbSignatureZoneTop(doc) {
  return doc.internal.pageSize.getHeight() - PTB_SIGNATURE_ZONE_H;
}

function col7MerchandisePtb(w) {
  const colQty = w * 0.095;
  const colDesc = w * 0.62;
  const subDesc = colDesc / 4;
  const colUnit = w * 0.095;
  const colTotal = w - colQty - colDesc - colUnit;
  return {
    0: { cellWidth: colQty, halign: 'left' },
    1: { cellWidth: subDesc, halign: 'left' },
    2: { cellWidth: subDesc, halign: 'left' },
    3: { cellWidth: subDesc, halign: 'left' },
    4: { cellWidth: subDesc, halign: 'left' },
    5: { cellWidth: colUnit, halign: 'right' },
    6: { cellWidth: colTotal, halign: 'right' },
  };
}

function estimateMerchBodyHeightPtb(doc, bodyRows, contentW) {
  const descW = contentW * 0.62 * 0.25;
  const cellPad = 2.4 * 2;
  let h = 0;
  for (const row of bodyRows) {
    if (row[0]?.styles?.minCellHeight != null) continue;
    const raw = row[1];
    const etiqueta =
      raw && typeof raw === 'object' ? String(raw.content || '') : String(raw || '');
    const lines = doc.splitTextToSize(etiqueta, descW);
    h += Math.max(7, cellPad + lines.length * 3.5);
  }
  return h;
}

function ptbMerchFillerRowCells(fillerH) {
  return Array.from({ length: 7 }, (_, col) => ({
    content: '',
    styles: {
      minCellHeight: col === 0 ? fillerH : undefined,
      valign: 'top',
      lineWidth: merchBodyVertLines,
    },
  }));
}

function merchBodyWithPageFillerPtb(doc, bodyRows, opts) {
  const { merchStartY, contentW, footerBelowMerchH } = opts;
  const zoneTop = ptbSignatureZoneTop(doc);
  const natural = estimateMerchBodyHeightPtb(doc, bodyRows, contentW);
  const reservedBelow = footerBelowMerchH + PTB_FILLER_SAFETY_MM;
  const need = merchStartY + PTB_MERCH_HEAD_EST_H + natural + reservedBelow;
  if (need > zoneTop) return bodyRows;
  let fillerH =
    zoneTop - merchStartY - PTB_MERCH_HEAD_EST_H - reservedBelow - natural;
  if (fillerH <= 3) return bodyRows;
  return [...bodyRows, ptbMerchFillerRowCells(fillerH)];
}

function drawPtbSignatureAtPageBottom(doc, pageW, margin) {
  const pageH = doc.internal.pageSize.getHeight();
  doc.setPage(doc.getNumberOfPages());
  const sigBlockW = 48;
  const sigCenterX = pageW - margin - sigBlockW / 2;
  const lineY = pageH - PTB_SIGNATURE_ZONE_H + 12;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(pageW - margin - sigBlockW + 2, lineY, pageW - margin - 2, lineY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('FIRMA Y TIMBRE', sigCenterX, lineY + 3.2, { align: 'center' });
  doc.text('(SIGNATURE)', sigCenterX, lineY + 6.5, { align: 'center' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.text('p.p. Palm Trade Brasil Impo. E Expo. Ltda.', sigCenterX, lineY + 10, {
    align: 'center',
  });
}

const RIGHT_LOGISTICS_ROWS = 4;

/** Bloque cliente izquierdo (misma geometría que PTC, etiquetas bilingües PTB). */
function drawLeftCustomerBlockPtb(doc, opts) {
  const { startX, startY, blockW, labelW, nombre, taxId, addrLines, pinCode } = opts;
  const padX = 1.5;
  const padY = 1.5;
  const fontSize = 8;
  const lineH = 2.8;
  const labelX = startX + padX;
  const valueX = startX + labelW + padX * 0.5;
  const valueW = blockW - labelW - padX * 2.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(fontSize);

  let cy = startY + padY + 3;

  const row = (label, value) => {
    doc.text(label, labelX, cy);
    doc.text(value || '', valueX, cy);
    cy += lineH;
  };

  row('Mrs./Señor(es):', nombre);
  row('Tax ID:', taxId);

  let addressStarted = false;
  const linesToDraw = addrLines.length ? addrLines : [''];
  linesToDraw.forEach((line) => {
    const wrapped = doc.splitTextToSize(line || '', valueW);
    wrapped.forEach((part) => {
      if (!addressStarted) {
        doc.text('Dirección/Address:', labelX, cy);
        addressStarted = true;
      }
      doc.text(part, valueX, cy);
      cy += lineH;
    });
  });
  if (!addressStarted) {
    doc.text('Dirección/Address:', labelX, cy);
    cy += lineH;
  }

  row('Phone:', opts.phone != null && opts.phone !== '' ? opts.phone : '22-2190119');
  row('Fax:', opts.fax != null ? opts.fax : '');
  row('P.O. Box:', pinCode);

  return cy + padY;
}

function drawRightLogisticsBlockPtb(doc, opts) {
  const { startY, x, width, params, blockHeight, closeBottomAt } = opts;
  const baseRowH = Math.floor((blockHeight / RIGHT_LOGISTICS_ROWS) * 100) / 100;
  const lastRowH = blockHeight - baseRowH * (RIGHT_LOGISTICS_ROWS - 1);
  const label7 = { fontSize: 7, halign: 'center', valign: 'middle' };

  autoTable(doc, {
    startY,
    margin: { left: x, right: doc.internal.pageSize.getWidth() - x - width },
    tableWidth: width,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: { top: 1.2, right: 1.2, bottom: 1.2, left: 1.2 },
      lineColor: [0, 0, 0],
      lineWidth: 0.15,
      textColor: [0, 0, 0],
      valign: 'middle',
      halign: 'center',
    },
    didParseCell(data) {
      if (data.section === 'body') {
        const isLast = data.row.index === RIGHT_LOGISTICS_ROWS - 1;
        data.cell.height = isLast ? lastRowH : baseRowH;
        if (isLast && closeBottomAt) {
          data.cell.styles.lineWidth = {
            top: 0.15,
            right: 0.15,
            bottom: 0,
            left: 0.15,
          };
        }
      }
    },
    body: [
      [
        { content: 'País de Origen\nOrigin Country', styles: label7 },
        { content: 'País de Destino\nCountry Destination', styles: label7 },
      ],
      [{ content: upper(params.paisOrigen) }, { content: upper(params.paisDestino) }],
      [
        { content: 'Puerto de Embarque\nLoading Port', styles: label7 },
        { content: 'Puerto de destino\nUnloading Port', styles: label7 },
      ],
      [
        { content: upper(params.puertoOrigen) },
        { content: upper(params.puertoDestino) },
      ],
    ],
    columnStyles: {
      0: { cellWidth: width / 2 },
      1: { cellWidth: width / 2 },
    },
  });
  const finalY = doc.lastAutoTable?.finalY ?? startY + blockHeight;
  return { finalY, left: x, right: x + width };
}

/** Cierra borde inferior del bloque logístico y prolonga verticales si autotable queda corto. */
function sealRightLogisticsBottom(doc, tableBox, blockEndY) {
  const { left, right, finalY } = tableBox;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.15);
  doc.line(left, blockEndY, right, blockEndY);
  if (finalY < blockEndY - 0.02) {
    const midX = (left + right) / 2;
    doc.line(left, finalY, left, blockEndY);
    doc.line(midX, finalY, midX, blockEndY);
    doc.line(right, finalY, right, blockEndY);
  }
}

/** Params PTB con mercancía + precios (misma fuente que PTC). */
export function invoicePtbParamsFromFormAndDetail(
  datosFormulario,
  plLineas,
  proforma,
  overrides = {}
) {
  const f = datosFormulario || {};
  const merc = buildInvoicePtcMerchandise(plLineas || [], proforma, {
    currency: f.invoiceCurrency || 'USD',
    fechaInvoice: f.fechaInvoice,
    fechaEmbarque: f.fechaEmbarque,
    numeroInvoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtb ?? '',
    productDescriptionSnapshot: f.productDescriptionSnapshot || '',
  });
  const base = invoicePtbParamsFromForm(datosFormulario, overrides);
  const lineas = mapLineasMercanciaToPtbRows(merc.lineasMercancia);
  const totalUsd =
    merc.totalUsd > 0
      ? merc.totalUsd
      : overrides.valorUsd != null && Number(overrides.valorUsd) > 0
        ? Number(overrides.valorUsd)
        : base.valorUsd;
  return {
    ...base,
    lineas,
    valorUsd: totalUsd,
    totalM3: merc.totalM3 ?? base.totalM3,
    quantity: f.quantity || String(merc.totalM3 || ''),
  };
}

export async function invoicePtbParamsFromApiWithPackingList(
  inv,
  { apiBase, headers, proformaEnMemoria = null }
) {
  const base = invoicePtbParamsFromApi(inv);
  const idPl = inv?.id_packing_list;
  const f = inv?.datos_formulario || {};
  if (!idPl || !apiBase || !headers) return base;
  try {
    const plRes = await fetch(`${apiBase}/packinglists/${idPl}`, { headers });
    if (!plRes.ok) return base;
    const pl = await plRes.json();
    const idProformas = resolveProformaIds(pl, f);
    let proforma = await fetchProformaUltima(
      apiBase,
      headers,
      idProformas,
      proformaEnMemoria
    );
    if (!proforma && pl.id_ordencompra) {
      proforma = await fetchOrdenCompraById(apiBase, headers, pl.id_ordencompra);
    }
    const merc = buildInvoicePtcMerchandise(pl.lineas || [], proforma, {
      currency: f.invoiceCurrency || 'USD',
      fechaInvoice: inv?.fecha_invoice || f.fechaInvoice,
      fechaEmbarque: pl.fecha_embarque || f.fechaEmbarque,
      numeroInvoice: inv?.numero_invoice || f.numeroIdentificadorPtb || '',
      productDescriptionSnapshot:
        pl.product_description_snapshot || f.productDescriptionSnapshot || '',
    });
    const lineas = mapLineasMercanciaToPtbRows(merc.lineasMercancia);
    const totalUsd =
      merc.totalUsd > 0
        ? merc.totalUsd
        : inv?.valor_usd != null && Number(inv.valor_usd) > 0
          ? Number(inv.valor_usd)
          : f?.totalUsd > 0
          ? Number(f.totalUsd)
          : base.valorUsd;
    return {
      ...base,
      lineas,
      valorUsd: totalUsd,
      totalM3: merc.totalM3 ?? plTotalsFromPl(pl),
      quantity: f.quantity || String(merc.totalM3 || ''),
      nombreBl: base.nombreBl || pl.nombre_bl || '',
      nombreNave: base.nombreNave || pl.nombre_nave || '',
      paisOrigen: base.paisOrigen || pl.pais_origen || '',
      paisDestino: base.paisDestino || pl.pais_destino || '',
      puertoOrigen: base.puertoOrigen || pl.nombre_puerto_origen || '',
      puertoDestino: base.puertoDestino || pl.nombre_puerto_destino || '',
    };
  } catch (err) {
    console.warn('invoicePtbParamsFromApiWithPackingList:', err);
    return base;
  }
}

function plTotalsFromPl(pl) {
  const t = packingListTotals(pl?.lineas || []);
  return t.totalM3;
}

export async function invoicePtbParamsFromFormWithPackingList(
  datosFormulario,
  overrides = {},
  { apiBase, headers, idPackingList, proformaEnMemoria = null, plLineas = null }
) {
  const f = datosFormulario || {};
  const inv = {
    id_packing_list: idPackingList,
    numero_invoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtb ?? '',
    fecha_invoice: f.fechaInvoice,
    empresa_emisor: f.empresaEmisor,
    datos_formulario: f,
  };
  const sync = invoicePtbParamsFromFormAndDetail(
    f,
    plLineas,
    proformaEnMemoria,
    overrides
  );
  if (lineasPdfTienenPrecios(sync.lineas)) return sync;
  return invoicePtbParamsFromApiWithPackingList(inv, {
    apiBase,
    headers,
    proformaEnMemoria,
  });
}

export function invoicePtbParamsFromForm(datosFormulario, overrides = {}) {
  const f = datosFormulario || {};
  const valorUsd =
    overrides.valorUsd != null
      ? overrides.valorUsd
      : f.totalUsd != null
        ? Number(f.totalUsd)
        : null;
  return invoicePtbParamsFromApi({
    numero_invoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtb ?? '',
    fecha_invoice: f.fechaInvoice,
    empresa_emisor: f.empresaEmisor,
    nombre_cliente: f.clienteNombre,
    nombre_bl: f.nombreBl,
    pais_destino: f.paisDestino,
    valor_usd: valorUsd,
    contrato_pi: f.contractNo,
    total_m3: f.totalM3,
    datos_formulario: f,
  });
}

/** PTB con emisor PTC (0): factura Brasil → Chile. PTB solo (1): cliente del formulario. */
export function esPtbHaciaPtc(inv) {
  const emisor = inv?.empresa_emisor;
  if (emisor != null && emisor !== '') return Number(emisor) !== 1;
  const f = inv?.datos_formulario || {};
  if (f.empresaEmisor != null && f.empresaEmisor !== '') return Number(f.empresaEmisor) !== 1;
  return false;
}

function aplicarDestinatarioSegunEmisor(params, inv) {
  if (!esPtbHaciaPtc(inv)) {
    return { ...params, ptbHaciaPtc: false };
  }
  const d = PTC_DESTINATARIO_EN_PTB;
  return {
    ...params,
    ptbHaciaPtc: true,
    clienteNombre: d.clienteNombre,
    taxId: d.taxId,
    clienteDireccion: d.clienteDireccion,
    pinCode: d.pinCode,
    clientePhone: d.clientePhone,
    clienteFax: '',
    remitPaymentText: d.remitPaymentText,
  };
}

export function invoicePtbParamsFromApi(inv) {
  const f = inv?.datos_formulario || {};
  const lineas = lineasFromFormulario(f, inv);
  const sumProducts = lineas.reduce((s, ln) => s + (Number(ln.totalValueUsd) || 0), 0) || null;
  const totalUsd =
    inv?.valor_usd != null
      ? Number(inv.valor_usd)
      : f?.totalUsd != null
        ? Number(f.totalUsd)
        : sumProducts;

  const esPtbEmisor = inv?.empresa_emisor === 1;
  const base = {
    numeroInvoice: inv?.numero_invoice || f?.numeroIdentificadorPtb || '',
    fechaInvoice: inv?.fecha_invoice || f?.fechaInvoice,
    issueCity: 'Santiago',
    clienteNombre: inv?.nombre_cliente || f?.clienteNombre || '',
    clienteDireccion: f?.clienteDireccion || '',
    taxId: f?.taxId || '',
    pinCode: f?.pinCode || '',
    clientePhone: f?.clientePhone || '',
    clienteFax: f?.clienteFax || '',
    paisOrigen: f?.paisOrigen || '',
    paisDestino: inv?.pais_destino || f?.paisDestino || '',
    puertoOrigen: f?.puertoOrigen || '',
    puertoDestino: f?.puertoDestino || '',
    lugarEntrega: f?.lugarEntrega || '',
    nombreNave: f?.nombreNave || '',
    nombreBl: inv?.nombre_bl || f?.nombreBl || '',
    contractNo: inv?.contrato_pi || f?.contractNo || '',
    purchaseOrder: esPtbEmisor
      ? f?.purchaseOrder || ''
      : f?.purchaseOrderPtb || f?.purchaseOrder || 'PTB-01',
    termsOfSale: esPtbEmisor ? f?.salesTerms || 'AFIRME' : f?.termsOfSalePtb || f?.salesTerms || 'AFIRME',
    clause: f?.clausePtb || '',
    paymentTerms: esPtbEmisor
      ? f?.paymentTermsPtc || ''
      : f?.paymentTermsPtb || f?.paymentTermsPtc || '',
    netWeight: f?.netWeight,
    grossWeight: f?.grossWeight,
    quantity: f?.quantity || '',
    valorUsd: totalUsd,
    totalM3: f?.totalM3 ?? inv?.total_m3,
    lineas,
    remitPaymentText:
      'Please Remit Payment to    a: Palm Trade Brasil   Impo.   E    Expo. Ltda',
  };
  return aplicarDestinatarioSegunEmisor(base, inv);
}

export function buildInvoicePtbPdfDoc(params) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentW = pageW - 2 * margin;
  const tableMargin = { left: margin, right: margin, bottom: PTB_SIGNATURE_ZONE_H };
  const logoY = 5;
  const boxY = 6;
  let y = boxY;

  const logoW = 34;
  const imgProps = doc.getImageProperties(ptbImage);
  const logoH = (imgProps.height * logoW) / imgProps.width;
  doc.addImage(ptbImage, 'JPEG', margin, logoY, logoW, logoH);

  const infoX = margin + 2;
  let infoY = logoY + logoH + 2;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  COMPANY_LINES.forEach((ln) => {
    doc.text(ln, infoX, infoY);
    infoY += 3.2;
  });

  const boxW = 60;
  const boxX = pageW - margin - boxW;
  autoTable(doc, {
    startY: boxY,
    margin: { left: boxX, right: margin },
    tableWidth: boxW,
    theme: 'plain',
    styles: {
      fontSize: 7,
      cellPadding: { top: 1.4, right: 0, bottom: 1.4, left: 0 },
      lineColor: [0, 0, 0],
      lineWidth: 0,
      textColor: [0, 0, 0],
      valign: 'middle',
      halign: 'center',
    },
    body: [
      [{ content: `CNPJ ${CNPJ_EMPRESA}`, styles: { fontSize: 9, fontStyle: 'bold' } }],
      [{ content: 'COMERCIAL INVOICE', styles: { fontSize: 7, fontStyle: 'normal' } }],
      [
        {
          content: `INVOICE N° ${invoiceNumberForHeader(params.numeroInvoice) || '—'}`,
          styles: { fontSize: 10, fontStyle: 'bold' },
        },
      ],
    ],
  });
  const yBoxEnd = doc.lastAutoTable.finalY;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.rect(boxX, boxY, boxW, yBoxEnd - boxY);

  y = Math.max(infoY + 1, yBoxEnd + 5.2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(
    `FECHA DE EMISION / ISSUE DATE: ${params.issueCity || 'Santiago'}, ${fmtDateLongEn(params.fechaInvoice)}`,
    margin,
    y + 3.5
  );
  y += 6;

  const leftW = contentW * 0.62;
  const rightW = contentW - leftW;
  const leftLabelW = leftW * 0.37;
  const addrLines = safe(params.clienteDireccion)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l) => !/^PIN CODE\s*:/i.test(l))
    .map((l) => upper(l));
  const blockStartY = y;
  const leftEndY = drawLeftCustomerBlockPtb(doc, {
    startX: margin,
    startY: blockStartY,
    blockW: leftW,
    labelW: leftLabelW,
    nombre: upper(params.clienteNombre),
    taxId: upper(params.taxId),
    addrLines,
    pinCode: safe(params.pinCode).trim(),
    phone: params.clientePhone,
    fax: params.clienteFax,
  });
  const blockH = leftEndY - blockStartY;
  const rightX = margin + leftW;
  const rightTable = drawRightLogisticsBlockPtb(doc, {
    startY: blockStartY,
    x: rightX,
    width: rightW,
    params,
    blockHeight: blockH,
    closeBottomAt: leftEndY,
  });
  const blockEndY = Math.max(leftEndY, rightTable.finalY);
  sealRightLogisticsBottom(doc, rightTable, blockEndY);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.15);
  doc.rect(margin, blockStartY, leftW, blockEndY - blockStartY);
  y = blockEndY;

  const labelCell = { fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 7 };
  const netFmt = fmtNumBr(params.netWeight, 3);
  const grossFmt = fmtNumBr(params.grossWeight, 3);
  const paymentTxt = safe(params.paymentTerms);

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        { content: 'Buque\nVessel', styles: labelCell },
        { content: 'N° Contrato\nContract No.', styles: labelCell },
        { content: 'Orden de Compra\nPurchase Order', styles: labelCell },
        { content: 'N° Doc. Embarque\nBill of Lading', styles: labelCell },
      ],
      [
        { content: upper(params.nombreNave) },
        { content: upper(params.contractNo) },
        { content: upper(params.purchaseOrder) },
        { content: upper(params.nombreBl) },
      ],
    ],
    columnStyles: col4Vessel(contentW),
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        { content: 'Modalidad de la Venta\nTerms of Sale', styles: labelCell },
        { content: 'Cláusula de Venta\nClause', styles: labelCell },
        { content: 'Forma de Pago\nPayment Terms', styles: labelCell },
        {
          content: 'TOTAL KILOS\nTOTAL WEIGHT (kg)',
          colSpan: 2,
          styles: labelCell,
        },
      ],
      [
        {
          content: upper(params.termsOfSale),
          rowSpan: 2,
          styles: { valign: 'middle', fontSize: 7 },
        },
        {
          content: upper(params.clause),
          rowSpan: 2,
          styles: { valign: 'middle', fontSize: 7 },
        },
        {
          content: paymentTxt,
          rowSpan: 2,
          styles: { halign: 'center', valign: 'middle', fontSize: 6.5 },
        },
        { content: 'Kgs. Netos\nNet Weight', styles: labelCell },
        { content: 'Kgs. Brutos\nGross Weight', styles: labelCell },
      ],
      [
        { content: netFmt, styles: { valign: 'middle' } },
        { content: grossFmt, styles: { valign: 'middle' } },
      ],
    ],
    columnStyles: col5SalesWeight(contentW),
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        {
          content:
            params.remitPaymentText ||
            'Please Remit Payment to    a: Palm Trade Brasil   Impo.   E    Expo. Ltda',
          colSpan: 5,
          styles: { fontStyle: 'bold', halign: 'center', fontSize: 7.5, valign: 'middle' },
        },
      ],
    ],
    columnStyles: col5SalesWeight(contentW),
  });
  y = doc.lastAutoTable.finalY;

  const lineas = params.lineas || [];
  const bodyMerchBase = lineas.map((ln) => [
    ln.quantityM3 != null ? `${fmtNumBr(ln.quantityM3, 3)}  M3` : '',
    { content: ln.etiqueta || '', styles: { valign: 'top', halign: 'left' } },
    ln.espessura || '',
    ln.largura || '',
    ln.comprimento || '',
    priceCellPtb(ln.unitPriceUsd),
    priceCellPtb(ln.totalValueUsd),
  ]);
  const bodyMerchRows = bodyMerchBase.length
    ? bodyMerchBase
    : [['—', '—', '', '', '', '', '']];

  const totalUsdNum = params.valorUsd != null ? Number(params.valorUsd) : null;
  const dueWords = totalUsdNum != null ? totalDueWordsPtb(totalUsdNum) : '';
  const dueText = dueWords ? `TOTAL DUE: ${dueWords}` : 'TOTAL DUE: —';
  const dueLines = doc.splitTextToSize(dueText, contentW - 4);
  const dueFooterH = Math.max(16, 8 + dueLines.length * 3.4 + 6);
  const footerBelowMerchH = PTB_TOTALS_ROW_EST_H + dueFooterH + 1;
  const merchStartY = y;
  const bodyMerch = merchBodyWithPageFillerPtb(doc, bodyMerchRows, {
    merchStartY,
    contentW,
    footerBelowMerchH,
  });

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    head: [
      [
        {
          content: 'Cantidad\nQuantity',
          styles: { ...merchHeadLabel, halign: 'left' },
        },
        {
          content: 'DESCRIPCION MERCADERIA\nMERCHANDISE DESCRIPTION',
          colSpan: 4,
          styles: { ...merchHeadCaps, halign: 'left' },
        },
        {
          content: 'UNIT PRICE\nUS $',
          styles: { ...merchHeadCaps, halign: 'right' },
        },
        {
          content: 'VALOR TOTAL\nTOTAL VALUE\nUS $',
          styles: { ...merchHeadCaps, halign: 'right' },
        },
      ],
      [
        { content: 'M3', styles: { ...merchHeadLabel, halign: 'left' } },
        { content: 'Etiqueta', styles: { ...merchHeadLabel, halign: 'left' } },
        { content: 'Espessura\nmm', styles: { ...merchHeadLabel, halign: 'left' } },
        { content: 'Largura\nmm', styles: { ...merchHeadLabel, halign: 'left' } },
        { content: 'Comprimento\nmm', styles: { ...merchHeadLabel, halign: 'left' } },
        { content: 'us$/M3', styles: { ...merchHeadLabel, halign: 'right' } },
        { content: '', styles: { ...merchHeadLabel, halign: 'right' } },
      ],
    ],
    body: bodyMerch,
    bodyStyles: {
      valign: 'top',
      halign: 'left',
      lineWidth: merchBodyVertLines,
    },
    columnStyles: col7MerchandisePtb(contentW),
  });
  y = doc.lastAutoTable.finalY;

  const totalM3Raw =
    params.quantity !== '' && params.quantity != null
      ? params.quantity
      : params.totalM3 != null
        ? params.totalM3
        : lineas.reduce((s, ln) => s + (Number(ln.quantityM3) || 0), 0);
  const totalM3Fmt =
    totalM3Raw !== '' && totalM3Raw != null ? `${fmtNumBr(totalM3Raw, 3)} M3` : '—';
  const totalUsdFmt = totalUsdNum != null ? fmtNumBr(totalUsdNum, 2) : '';

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        {
          content: `Total M3: ${totalM3Fmt}`,
          colSpan: 5,
          styles: { fontStyle: 'bold', fontSize: 7, halign: 'left' },
        },
        { content: 'Total FOB USD', styles: { fontStyle: 'bold', halign: 'right' } },
        { content: totalUsdFmt, styles: { fontStyle: 'bold', halign: 'right' } },
      ],
    ],
    columnStyles: col7MerchandisePtb(contentW),
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        {
          content: ' ',
          colSpan: 7,
          styles: { minCellHeight: dueFooterH, valign: 'top' },
        },
      ],
    ],
    columnStyles: col7MerchandisePtb(contentW),
    didDrawCell(data) {
      if (data.section !== 'body' || data.row.index !== 0 || data.column.index !== 0) {
        return;
      }
      const { x, y: cellY } = data.cell;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text('TOTAL A PAGAR', x + 1.5, cellY + 4);
      doc.setFont('helvetica', 'normal');
      doc.text(dueLines, x + 1.5, cellY + 8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.text('( CON LETRA / WITH LETTERS )', x + 1.5, cellY + 8 + dueLines.length * 3.4 + 1);
    },
  });

  drawPtbSignatureAtPageBottom(doc, pageW, margin);

  return doc;
}

export function invoicePtbPdfFileName(params) {
  return `Invoice_PTB_${safe(params.numeroInvoice).replace(/\W/g, '_') || 'preview'}.pdf`;
}

export function createInvoicePtbPdfBlobUrl(params) {
  const doc = buildInvoicePtbPdfDoc(params);
  return URL.createObjectURL(doc.output('blob'));
}

export function generateInvoicePtbPdf(params) {
  buildInvoicePtbPdfDoc(params).save(invoicePtbPdfFileName(params));
}